(() => {
  const overlay = document.querySelector('[data-navigation-loading]');
  const pageContainer = document.getElementById('MainContent');

  if (!overlay || !pageContainer) return;

  const SHOW_DELAY_MS = 100;
  const RECOVERY_TIMEOUT_MS = 30000;
  const DESKTOP_STORES_QUERY = '(min-width: 990px)';
  const MOBILE_SEARCH_QUERY = '(max-width: 989px)';
  const locale = (document.documentElement.lang || '').toLowerCase().split('-')[0];
  const staticPageHandles = new Set([
    'about-us',
    'account-removal',
    'contact',
    'delivery',
    'gift-card',
    'help-center',
    'information',
  ]);
  const excludedPageHandles = new Set([
    'brands',
    'otp-login',
    'promotions',
    'store-locator',
    'stores',
    'technical',
    'technical-browser',
    'technical-cookies',
    'technical-offline',
    'technical-server-error',
    'technical-vpn',
    'wishlist',
  ]);

  let showTimer = 0;
  let recoveryTimer = 0;
  let activeAnchor = null;
  let activeClick = null;
  let activeForm = null;
  let activeSubmit = null;
  let previousBusy = null;

  const visibleHeaderOffset = () => {
    const candidates = [
      document.querySelector('.announcement-bar'),
      document.querySelector('[data-site-header]'),
    ].filter(Boolean);

    return candidates.reduce((offset, element) => {
      const rect = element.getBoundingClientRect();
      if (rect.bottom <= 0 || rect.top >= window.innerHeight) return offset;
      return Math.max(offset, Math.min(window.innerHeight, rect.bottom));
    }, 0);
  };

  const normalizePath = (pathname) => {
    let path = pathname.replace(/\/{2,}/g, '/');
    const localePrefix = `/${locale}`;

    if (locale && path === localePrefix) path = '/';
    if (locale && path.startsWith(`${localePrefix}/`)) path = path.slice(localePrefix.length);
    if (path.length > 1) path = path.replace(/\/+$/, '');

    return path || '/';
  };

  const familyFor = (url) => {
    const path = normalizePath(url.pathname);

    if (path === '/') return 'home';
    if (path === '/cart') return 'cart';
    if (path === '/collections' || path.startsWith('/collections/')) return 'plp';
    if (path.startsWith('/products/')) return 'pdp';
    if (path === '/search' || path.startsWith('/search/')) {
      return window.matchMedia(MOBILE_SEARCH_QUERY).matches ? 'search' : null;
    }
    if (path === '/account') return 'account';
    if (path === '/pages/club-program') return 'bonus';

    if (path === '/pages/store-locator' || path === '/pages/stores') {
      return window.matchMedia(DESKTOP_STORES_QUERY).matches ? 'stores' : null;
    }

    if (
      path === '/checkout'
      || path.startsWith('/checkout/')
      || path.startsWith('/checkouts/')
      || path.startsWith('/account/')
      || path.startsWith('/cart/')
      || path === '/404'
    ) {
      return null;
    }

    if (path === '/policies' || path.startsWith('/policies/')) return 'static';
    if (path === '/blogs' || path.startsWith('/blogs/')) return 'static';

    if (path.startsWith('/pages/')) {
      const handle = path.slice('/pages/'.length);
      if (excludedPageHandles.has(handle)) return null;
      return staticPageHandles.has(handle) ? 'static' : null;
    }

    return null;
  };

  const isSameDocument = (url) => (
    url.pathname === window.location.pathname
    && url.search === window.location.search
  );

  const isExcludedAnchor = (anchor) => (
    anchor.hasAttribute('download')
    || (anchor.target && anchor.target.toLowerCase() !== '_self')
    || anchor.closest('[data-navigation-loading-exclude]')
    || anchor.closest('[data-otp-auth-trigger]')
    || (
      anchor.closest('[data-predictive-search-results]')
      && !anchor.closest('[data-mobile-search-results]')
    )
  );

  const clearTimers = () => {
    window.clearTimeout(showTimer);
    window.clearTimeout(recoveryTimer);
    showTimer = 0;
    recoveryTimer = 0;
  };

  const hide = (reason = 'reset') => {
    clearTimers();
    overlay.hidden = true;
    overlay.dataset.family = '';
    overlay.style.removeProperty('--le-navigation-loading-top');

    if (previousBusy === null) {
      pageContainer.removeAttribute('aria-busy');
    } else {
      pageContainer.setAttribute('aria-busy', previousBusy);
    }

    previousBusy = null;
    activeAnchor = null;
    activeClick = null;
    activeForm = null;
    activeSubmit = null;

    document.dispatchEvent(new CustomEvent('letoile:navigation-loading-hide', {
      detail: { reason },
    }));
  };

  const show = (family) => {
    previousBusy = pageContainer.getAttribute('aria-busy');
    pageContainer.setAttribute('aria-busy', 'true');
    overlay.style.setProperty('--le-navigation-loading-top', `${visibleHeaderOffset()}px`);
    overlay.dataset.family = family;
    overlay.hidden = false;

    document.dispatchEvent(new CustomEvent('letoile:navigation-loading-show', {
      detail: { family },
    }));

    recoveryTimer = window.setTimeout(() => hide('recovery-timeout'), RECOVERY_TIMEOUT_MS);
  };

  document.addEventListener('click', (event) => {
    if (
      event.button !== 0
      || event.metaKey
      || event.ctrlKey
      || event.shiftKey
      || event.altKey
    ) {
      return;
    }

    const anchor = event.target.closest?.('a[href]');
    if (!anchor || isExcludedAnchor(anchor)) return;

    let url;
    try {
      url = new URL(anchor.href, window.location.href);
    } catch {
      return;
    }

    if (
      !/^https?:$/.test(url.protocol)
      || url.origin !== window.location.origin
      || isSameDocument(url)
    ) {
      return;
    }

    const family = familyFor(url);
    if (!family) return;

    clearTimers();
    activeAnchor = anchor;
    activeClick = event;

    document.dispatchEvent(new CustomEvent('letoile:navigation-click', {
      detail: { family },
    }));

    showTimer = window.setTimeout(() => {
      if (
        activeClick?.defaultPrevented
        || !activeAnchor?.isConnected
        || document.visibilityState === 'hidden'
      ) {
        hide('navigation-cancelled');
        return;
      }

      show(family);
    }, SHOW_DELAY_MS);
  });

  document.addEventListener('submit', (event) => {
    const form = event.target.closest?.('form[action]');
    if (!form || form.method.toLowerCase() !== 'get') return;

    let url;
    try {
      url = new URL(form.action, window.location.href);
    } catch {
      return;
    }

    const family = familyFor(url);
    if (family !== 'search') return;

    clearTimers();
    activeForm = form;
    activeSubmit = event;
    showTimer = window.setTimeout(() => {
      if (
        activeSubmit?.defaultPrevented
        || !activeForm?.isConnected
        || document.visibilityState === 'hidden'
      ) {
        hide('navigation-cancelled');
        return;
      }

      show(family);
    }, SHOW_DELAY_MS);
  });

  window.addEventListener('pagehide', clearTimers);
  window.addEventListener('pageshow', () => hide('pageshow'));
  window.addEventListener('popstate', () => hide('popstate'));
  window.addEventListener('hashchange', () => hide('hashchange'));
  window.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !overlay.hidden) hide('escape');
  });
})();
